import { useMemo, useState } from "react";

export default function useErpDataList({
  items = [],
  searchFields = [],
  filters = {},
  filterFn,
  pageSize = 10,
}) {
  const [search, setSearchState] = useState("");
  const [sort, setSort] = useState({ key: "", desc: false });
  const [page, setPageState] = useState(1);

  const filteredItems = useMemo(() => {
    const query = search.trim().toLowerCase();

    let rows = items.filter((item) =>
      !query ||
      searchFields.some((field) => {
        const value = field.split(".").reduce((obj, key) => obj?.[key], item);
        return String(value ?? "").toLowerCase().includes(query);
      })
    );

    rows = rows.filter((item) => {
      const matchesFilters = Object.entries(filters).every(([key, value]) =>
        value === "" || value == null ||
        String(item?.[key] ?? "") === String(value)
      );
      return matchesFilters && (!filterFn || filterFn(item, filters));
    });

    if (sort.key) {
      const getValue = (item) =>
        sort.key.split(".").reduce((obj, key) => obj?.[key], item);

      rows = [...rows].sort((a, b) => {
        const result = String(getValue(a) ?? "").localeCompare(
          String(getValue(b) ?? ""), "id", { numeric: true }
        );
        return sort.desc ? -result : result;
      });
    }

    return rows;
  }, [items, search, searchFields, filters, filterFn, sort]);

  const safePageSize = Math.max(1, Number(pageSize) || 10);
  const totalItems = filteredItems.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / safePageSize));
  const currentPage = Math.min(page, totalPages);

  const setSearch = (value) => {
    setSearchState(value);
    setPageState(1);
  };

  const setPage = (value) => {
    setPageState((old) => {
      const next = typeof value === "function" ? value(old) : value;
      return Math.max(1, Math.min(Number(next) || 1, totalPages));
    });
  };

  const toggleSort = (key) => {
    setSort((old) => ({
      key,
      desc: old.key === key ? !old.desc : false,
    }));
    setPageState(1);
  };

  const reset = () => {
    setSearchState("");
    setSort({ key: "", desc: false });
    setPageState(1);
  };

  return {
    search,
    setSearch,
    sort,
    toggleSort,
    page: currentPage,
    setPage,
    pageSize: safePageSize,
    totalItems,
    totalPages,
    filteredItems,
    paginatedItems: filteredItems.slice(
      (currentPage - 1) * safePageSize,
      currentPage * safePageSize
    ),
    isEmpty: items.length === 0,
    hasNoResults: items.length > 0 && totalItems === 0,
    reset,
  };
}
